import { downloadFile } from '@api/Files'
import type { ThemeFromEditor } from '@api/hooks/themes'
import {
  config as defaultConfig,
  resources as defaultResources,
  replaceableColors as defaultReplaceableColors
} from './mocks/defaultTheme'

/** New palette: color1 → primary, color2 → secondary, color3 → highlight1 */
export type ColorPaletteApply = {
  color1: string
  color2: string
  color3: string
}

const THEME_FOLDER_PREFIX = 'THEME_FOLDER/'

type themeType = 'default'

type GetThemeConfigurationProps = {
  themeType: themeType
  primaryColor: string
  secondaryColor: string
  thirdColor: string
  /**
   * Base URL for theme assets (e.g. window.location.origin).
   * When provided, THEME_FOLDER/... values are resolved via the download API
   * and replaced with the returned storage paths.
   */
  themeAssetsBaseUrl?: string
}

export type ThemeConfigurationResult = {
  config: typeof defaultConfig
  resources: typeof defaultResources
}

const getThemeConfiguration = async ({
  themeType,
  primaryColor,
  secondaryColor,
  thirdColor,
  themeAssetsBaseUrl
}: GetThemeConfigurationProps): Promise<ThemeConfigurationResult> => {
  const replaceableColors = getReplaceableColorsByThemeType(themeType)
  const replacements: [string, string][] = [
    [replaceableColors.primary, primaryColor],
    [replaceableColors.secondary, secondaryColor],
    [replaceableColors.third, thirdColor]
  ]

  let config = replaceColorsInValue(
    defaultConfig,
    replacements
  ) as typeof defaultConfig
  let resources = replaceColorsInValue(
    defaultResources,
    replacements
  ) as typeof defaultResources

  if (themeAssetsBaseUrl) {
    const themePathMap = await resolveThemeFolderPaths(
      themeType,
      themeAssetsBaseUrl,
      config,
      resources
    )
    if (themePathMap.size > 0) {
      config = replaceThemePathsInValue(
        config,
        themePathMap
      ) as typeof defaultConfig
      resources = replaceThemePathsInValue(
        resources,
        themePathMap
      ) as typeof defaultResources
    }
  }

  return { config, resources }
}

/**
 * Collects all unique string values that start with THEME_FOLDER/
 * by traversing the structure recursively.
 */
function collectThemeFolderPaths(value: unknown): Set<string> {
  const paths = new Set<string>()
  function walk(v: unknown): void {
    if (typeof v === 'string') {
      if (v.startsWith(THEME_FOLDER_PREFIX)) paths.add(v)
      return
    }
    if (Array.isArray(v)) {
      v.forEach(walk)
      return
    }
    if (v !== null && typeof v === 'object') {
      Object.values(v).forEach(walk)
    }
  }
  walk(value)
  return paths
}

/**
 * Resolves THEME_FOLDER/... paths by calling the download API in parallel.
 * Each path is requested as: themeAssetsBaseUrl/images/corporate/themes/{themeType}/{filename}
 * Returns a map from original path (e.g. THEME_FOLDER/xxx.jpeg) to the API response path (URL).
 */
async function resolveThemeFolderPaths(
  themeType: themeType,
  themeAssetsBaseUrl: string,
  config: typeof defaultConfig,
  resources: typeof defaultResources
): Promise<Map<string, string>> {
  const allPaths = new Set<string>()
  collectThemeFolderPaths(config).forEach((p) => allPaths.add(p))
  collectThemeFolderPaths(resources).forEach((p) => allPaths.add(p))

  if (allPaths.size === 0) return new Map()

  const base = themeAssetsBaseUrl.replace(/\/$/, '')
  const themeBase = `${base}/images/corporate/themes/${themeType}`

  const results = await Promise.all(
    Array.from(allPaths).map(async (themePath) => {
      const filename = themePath.slice(THEME_FOLDER_PREFIX.length)
      const url = `${themeBase}/${filename}`
      try {
        const response = ((await downloadFile({ url })) as unknown) as {
          data: { path: string }
        }
        return [themePath, response.data.path] as const
      } catch {
        return [themePath, themePath] as const
      }
    })
  )

  return new Map(results)
}

/**
 * Recursively replaces THEME_FOLDER paths in a value with the resolved URLs.
 */
function replaceThemePathsInValue(
  value: unknown,
  pathMap: Map<string, string>
): unknown {
  if (typeof value === 'string') {
    return pathMap.get(value) ?? value
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceThemePathsInValue(item, pathMap))
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = replaceThemePathsInValue(v, pathMap)
    }
    return result
  }
  return value
}

function replaceColorsInValue(
  value: unknown,
  replacements: [string, string][]
): unknown {
  if (typeof value === 'string') {
    let result = value
    for (const [from, to] of replacements) {
      result = result.split(from).join(to)
    }
    return result
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceColorsInValue(item, replacements))
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = replaceColorsInValue(v, replacements)
    }
    return result
  }
  return value
}

const getReplaceableColorsByThemeType = (themeType: themeType) => {
  switch (themeType) {
    case 'default':
      return defaultReplaceableColors
    default:
      return defaultReplaceableColors
  }
}

/**
 * Replaces the theme's current primary, title and highlight1 colors with the
 * new palette everywhere in config and resources (same approach as getThemeConfiguration).
 * Uses theme.config.colors.primary, .title, .highlight1 as the "from" values (dynamic).
 * color1 → primary, color2 → title, color3 → highlight1.
 */

/**
 * Updates only config.palette. The template library applies colors at render time.
 */
export function applyColorPaletteToTheme(
  theme: ThemeFromEditor,
  colorPalette: ColorPaletteApply
): ThemeFromEditor {
  return {
    ...theme,
    config: {
      ...theme.config,
      palette: {
        ...theme.config.palette,
        primary: colorPalette.color1,
        secondary: colorPalette.color2,
        accent: colorPalette.color3
      }
    }
  }
}

export default getThemeConfiguration
